import type {CopilotUIMessage} from '@mixpanel/ai-copilot/agent/types';
import type {ChatStatus} from 'ai';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import type {LocalizationStore} from 'common/i18n/store';
import {getProjectId, getReportName, getReportUrl, getWorkspaceId} from 'common/report/util';
import {StateFromUrlUtils} from 'common/report/util/state-from-url';
import {ReportName} from 'common/report/constants';
import type {ConversationSummary} from 'common/copilot/conversations-api';
import {MAX_CONVERSATION_TITLE_LENGTH} from 'common/copilot/conversations-api';

export const COPILOT_CHAT_VIEW_QUERY_PARAM = `cp_chat_view`;
// Only meaningful for the panel surface — the full-page surface gets the id from the route param.
export const COPILOT_CONVERSATION_ID_QUERY_PARAM = `cp_conversation_id`;
export const COPILOT_SHARED_NUM_MESSAGES_QUERY_PARAM = `cp_num_messages`;
// Deep-link param: landing on the agent with this set auto-submits a canned
// first message on the user's behalf (see CHAT_INTENT_PROMPTS). One-shot — the
// param is stripped from the URL after firing so a refresh doesn't resubmit.
export const COPILOT_CHAT_INTENT_QUERY_PARAM = `chat_intent`;
// Set by the visual-editor CTA when navigating to the experiment page; the
// Edit-on-Site button reads it to auto-open its URL-input modal, then strips it.
export const OPEN_OVERLAY_QUERY_PARAM = `open_overlay`;

export enum CopilotChatIntent {
  // From the in-product onboarding flow: "help me get set up".
  UserOnboarding = `user_onboarding`,
}

// The message auto-submitted for each known intent. Kept in code (not the URL)
// so the deep link can't inject arbitrary prompts on the user's behalf.
export const CHAT_INTENT_PROMPTS: Record<CopilotChatIntent, string> = {
  [CopilotChatIntent.UserOnboarding]: `Help me get set up`,
};

// A friendlier title for an intent-started chat, applied after the first message
// is submitted so the history entry reads better than the canned prompt. Returns
// undefined for intents with no custom title (title then falls back to the first
// message). Takes the localization store so the label and date are localized.
export function chatIntentTitle(intent: CopilotChatIntent, i18n: LocalizationStore): string | undefined {
  if (intent === CopilotChatIntent.UserOnboarding) {
    // Locale-aware short date, e.g. "Jul 29, 2026".
    const date = i18n.formatDate(new Date(), {year: `numeric`, month: `short`, day: `numeric`});
    return i18n.t(`copilot.onboarding.chat-title`, {date}).slice(0, MAX_CONVERSATION_TITLE_LENGTH);
  }
  return undefined;
}

// Rendering the title key with an empty date yields the label onboarding titles start with.
function onboardingTitlePrefix(i18n: LocalizationStore): string {
  return i18n.t(`copilot.onboarding.chat-title`, {date: ``}).trim();
}

// The most recently updated conversation whose title starts with the localized onboarding label.
export function findOnboardingConversation(
  conversations: ConversationSummary[],
  i18n: LocalizationStore,
): ConversationSummary | undefined {
  const prefix = onboardingTitlePrefix(i18n);
  if (!prefix) return undefined; // never match everything on a degenerate/empty label
  return (
    conversations
      .filter((c) => c.title.startsWith(prefix))
      // updated_at is ISO-8601, which sorts correctly as a string.
      .reduce<ConversationSummary | undefined>(
        (best, c) => (!best || c.updated_at > best.updated_at ? c : best),
        undefined,
      )
  );
}

function toChatIntent(raw: string | null): CopilotChatIntent | undefined {
  const intents = Object.values(CopilotChatIntent) as string[];
  return raw && intents.includes(raw) ? (raw as CopilotChatIntent) : undefined;
}

/** Reads a recognized `chat_intent` from the URL, or undefined when absent/unknown. */
export function parseChatIntent(search: string = window.location.search): CopilotChatIntent | undefined {
  return toChatIntent(new URLSearchParams(search).get(COPILOT_CHAT_INTENT_QUERY_PARAM));
}

export enum CopilotChatView {
  // Normal active chat (the fallback when cp_chat_view is absent or unrecognized).
  Default = `default`,
  // View-only shared snapshot.
  Shared = `shared`,
}

export enum CopilotLoadError {
  Generic = `generic`,
  Private = `private`,
}

export enum CopilotLocation {
  // Side-panel share lives on whatever app page the sharer was on, so the
  // conversation id rides along as a query param (the URL path belongs to that app).
  Panel = `panel`,
  // Full-page share lives on /app/agent/<id>; the id is a route param.
  FullPage = `full-page`,
}

export type PanelQueryParams = {
  /** cp_chat_view — `default` when absent or unrecognized. */
  chatView: CopilotChatView;
  /** cp_conversation_id, if present. */
  conversationId?: string;
  /** cp_num_messages — shared snapshot bound; only the shared view sets it (undefined = all messages). */
  sharedNumMessages?: number;
};

export type FullPageQueryParams = {
  /** cp_chat_view — `default` when absent or unrecognized. */
  chatView: CopilotChatView;
  /** cp_num_messages — shared snapshot bound; only the shared view sets it (undefined = all messages). */
  sharedNumMessages?: number;
};

function parseNonNegInt(raw: string | null): number | undefined {
  // Treat an empty value (`cp_num_messages=`) as absent — Number(``) is 0,
  // which would otherwise turn a mangled link into a zero-message snapshot.
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

function parseChatView(raw: string | null): CopilotChatView {
  const modes = Object.values(CopilotChatView) as string[];
  return raw && modes.includes(raw) ? (raw as CopilotChatView) : CopilotChatView.Default;
}

// The snapshot bound only applies to a shared view; ignored otherwise.
function parseSharedNumMessages(params: URLSearchParams, chatView: CopilotChatView): number | undefined {
  if (chatView !== CopilotChatView.Shared) return undefined;
  return parseNonNegInt(params.get(COPILOT_SHARED_NUM_MESSAGES_QUERY_PARAM));
}

/**
 * Parses copilot query params for the side-panel surface (any app page). The
 * conversation id lives in the query string because the URL path is owned by the
 * host app (insights, boards, etc.).
 */
export function parsePanelQueryParams(search: string = window.location.search): PanelQueryParams {
  const params = new URLSearchParams(search);
  const chatView = parseChatView(params.get(COPILOT_CHAT_VIEW_QUERY_PARAM));
  return {
    chatView,
    conversationId: params.get(COPILOT_CONVERSATION_ID_QUERY_PARAM) ?? undefined,
    sharedNumMessages: parseSharedNumMessages(params, chatView),
  };
}

/**
 * Parses copilot query params for the full-page surface (/app/agent/:id). The
 * conversation id is the route param, supplied separately by the caller — not
 * parsed here.
 */
export function parseFullPageQueryParams(search: string = window.location.search): FullPageQueryParams {
  const params = new URLSearchParams(search);
  const chatView = parseChatView(params.get(COPILOT_CHAT_VIEW_QUERY_PARAM));
  return {
    chatView,
    sharedNumMessages: parseSharedNumMessages(params, chatView),
  };
}

// Centralized so "is a turn currently in flight" can't drift between call sites.
export function isTurnInFlight(status: ChatStatus): boolean {
  return status === `submitted` || status === `streaming`;
}

export function getCopyableTextFromMessage(message: CopilotUIMessage): string {
  return message.parts
    .map((part) => {
      if (part.type === `text`) {
        return part.text.trim();
      }
      // TODO: Add support for other message part types.
      return ``;
    })
    .filter(Boolean)
    .join(`\n`);
}

/** True anywhere on the full-page surface — the bare `/app/agent` index or a conversation route. */
export function isFullPagePath(): boolean {
  const urlState = StateFromUrlUtils.parseStateFromUrl();
  return getReportName(urlState?.appName) === ReportName.Agent;
}

/** True when the path is a full-page conversation route (`/app/agent/<id>`), not the bare `/app/agent` index. */
export function isFullPageConversationPath(): boolean {
  const urlState = StateFromUrlUtils.parseStateFromUrl();
  return getReportName(urlState?.appName) === ReportName.Agent && !!urlState?.appPath && urlState.appPath !== `/`;
}

export function fullPagePath(): string {
  return getReportUrl(`agent`, {
    projectId: Number(getProjectId()),
    workspaceId: Number(getWorkspaceId()),
  });
}

// Agent deep link for a freshly created project. Takes the project id (and optional
// workspace id) explicitly rather than reading getProjectId()/getWorkspaceId() from
// the URL, because the caller (org/project creation) is still on the signup surface
// whose globals point at the old context. Hardcodes the onboarding intent so the
// agent auto-submits the "help me get set up" prompt on arrival.
export function onboardingAgentPath(projectId: number, workspaceId?: number): string {
  return getReportUrl(`agent`, {projectId, workspaceId}, true, {
    [COPILOT_CHAT_INTENT_QUERY_PARAM]: CopilotChatIntent.UserOnboarding,
  });
}

// Bare path, no query string — callers append their own search/hash. ignoreSearch
// drops getReportUrl's persisted params (mp_source, feature-flags, …) so they can't
// land spliced in front of the id.
export function conversationPath(id: string): string {
  return getReportUrl(`agent/${id}`, {projectId: Number(getProjectId()), workspaceId: Number(getWorkspaceId())}, true);
}

/** Sets cp_chat_view + cp_num_messages; the bound is omitted when unset (= all messages). */
export function setSharedViewParams(params: URLSearchParams, sharedNumMessages?: number): void {
  params.set(COPILOT_CHAT_VIEW_QUERY_PARAM, CopilotChatView.Shared);
  if (Number.isFinite(sharedNumMessages)) {
    params.set(COPILOT_SHARED_NUM_MESSAGES_QUERY_PARAM, String(sharedNumMessages));
  }
}

/** Relative full-page URL for a shared conversation, e.g. `/project/1/app/agent/<id>?cp_chat_view=shared&cp_num_messages=12`. */
export function sharedConversationPath(id: string, sharedNumMessages?: number): string {
  const search = new URLSearchParams();
  setSharedViewParams(search, sharedNumMessages);
  return `${conversationPath(id)}?${search.toString()}`;
}

export function getCopilotURLParams() {
  const dict = omitBy(
    {
      project_id: getProjectId() || undefined,
      workspace_id: getWorkspaceId() || undefined,
    },
    isNil,
  );

  const urlParams = new URLSearchParams(dict as Record<string, string>);

  return urlParams.toString();
}
